import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as AlertSVG } from "go-core/images/svg/alert.svg";
import FormatResourceStatus from "../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import Tag from "../../../../../../../../components/Common/Tag/Tag";
import { SubscriptionApi } from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { RenderApplicationLogo } from "../../../../../../components/RenderApplicationLogo/RenderApplicationLogo";
import SubscriptionDate from "./SubscriptionDate";
import { getSubscriptionExternalId, isSubscriptionUninstalled } from "./utils";

interface Props {
	sub: SubscriptionApi;
}

export const splitDateWithComma = (date: string) => {
	return date?.split(",")[0];
};

const SingleSubscriptionPreview = ({ sub }: Props) => {
	const { t } = useTranslation();
	const status = sub.status === "NON_RENEWING" ? "CANCELLED" : sub.status;

	return (
		<tr className={`subscription-item ${isSubscriptionUninstalled(sub) && "subscription-item--uninstalled"}`}>
			<td className={"w-20"}>
				<div className={"subscription-details"}>
					<RenderApplicationLogo slug={sub.family_id} />
					<div>
						<h5>{sub.name}</h5>
						<small className="text-muted">{getSubscriptionExternalId(sub)}</small>
					</div>
				</div>
			</td>
			<td className={"w-20"}>
				<div className={"subscription-status"}>
					<div className={"d-flex"}>
						<FormatResourceStatus
							status={
								status === "CANCELLED" && sub.cancellation_reason === "WAITING_PAYMENT"
									? "EXPIRED"
									: status
							}
						/>
						{sub.status === "CANCELLED" && (
							<OverlayTrigger
								placement="top"
								overlay={
									<Tooltip id={`tooltip-${sub.family_id}`}>
										{sub.cancellation_reason !== "OTHER" &&
											t(`constraints.SUBSCRIPTION_${sub.cancellation_reason}`)}
										{sub.cancellation_reason === "OTHER" &&
											t(`constraints.SUBSCRIPTION_${sub.cancellation_reason}`)}
										{sub.cancellation_reason === "OTHER" && sub.reason_details && (
											<>: {sub.reason_details}</>
										)}
									</Tooltip>
								}
							>
								{({ ref, ...triggerHandler }) => (
									<AlertSVG className={"ms-1"} ref={ref} {...triggerHandler} />
								)}
							</OverlayTrigger>
						)}
					</div>
					<SubscriptionDate subscription={sub} className="subscription-date" />
				</div>
			</td>
			<td>
				{sub.monthly_sub_item && (
					<div className={"addon-type-label"}>
						<span className={"label"}>{t("modules.subscription.field.yearly_female.title")}</span>
					</div>
				)}
				<div className={"subscription-addons"}>
					{sub.addons.length > 0 ? (
						sub.addons?.map((addon, index) => {
							return (
								<Tag key={index}>
									{addon.name}
									{(addon.quantity || addon.quantity === 0) && `: ${addon.quantity}`}
								</Tag>
							);
						})
					) : (
						<Tag styles={{ color: "#6C757D" }}>{t("modules.subscription.field.no_additions.title")}</Tag>
					)}
				</div>
				{sub.monthly_sub_item && (
					<div className={"addon-type-label"}>
						<span className="label">{t("modules.subscription.field.monthly_female.title")}</span>
					</div>
				)}
				<div className={"subscription-addons"}>
					{sub.monthly_sub_item?.addons.map((addon, index) => {
						return (
							<Tag key={index}>
								{addon.name}
								{addon.quantity && `: ${addon.quantity}`}
							</Tag>
						);
					})}
				</div>
			</td>
			<td className={"nowrap"}>
				<div className={"subscription-money"}>
					<h5>
						{FormatMoney(sub.total_money)}/{sub.billing_period === "YEARLY" ? "rok" : "mies."}
					</h5>
					{sub.monthly_sub_item && (
						<span className={"label"}>
							+ {FormatMoney(sub.monthly_sub_item.total_money)}/
							{t("modules.subscription.field.monthly_shortcut.title")}
						</span>
					)}
				</div>
			</td>
		</tr>
	);
};
export default SingleSubscriptionPreview;
